import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement } from 'chart.js';
import { Tooltip, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { LEGACY_COLORS } from '../../../constants/graphColors';
import analyseTranslation from '../../../constants/AnalyseTranslation.json';

ChartJS.register(ArcElement);

type AnalyseRecommendationProps = {
  recommendationDonutData: any;
  handleNavigation: (errorTypeId: number, segmentId?: number) => Promise<void>;
};

const options = {
  responsive: false,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
    },
  },
  cutout: '70%',
};

const generateData = (itemAllSum: number, itemTypeSum: number, color: string) => {
  const missingSum = itemAllSum - itemTypeSum;
  return {
    labels: ['Completed', 'Remaining'],
    datasets: [
      {
        data: [itemTypeSum, missingSum],
        backgroundColor: [color, LEGACY_COLORS.white],
        hoverBackgroundColor: [color, LEGACY_COLORS.white],
        borderColor: LEGACY_COLORS.lightGrey,
        hoverBorderColor: LEGACY_COLORS.blackOpacity,
      },
    ],
  };
};

const AnalyseRecommendation: React.FC<AnalyseRecommendationProps> = ({
  recommendationDonutData,
  handleNavigation,
}) => {
  const { t } = useTranslation();

  const checkIfAnalysesExist = () => {
    return <span className="analyses-not-existing">{analyseTranslation.no_analyses_existing}</span>;
  };

  const navigationButton = (
    title: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal | null | undefined,
    alertTypeId: number
  ) => {
    return (
      <Button onClick={() => handleNavigation(alertTypeId)} size="small">
        {title}
      </Button>
    );
  };

  const {
    item_error_count: itemErrorCount,
    item_info_count: itemInfoCount,
    item_warning_count: itemWarningCount,
    item_count: itemCount,
  } = recommendationDonutData;

  const dataAllRecommendation = generateData(
    itemCount || 1,
    itemInfoCount || 0,
    LEGACY_COLORS.blue
  );
  const dataWarnings = generateData(itemCount || 1, itemWarningCount, LEGACY_COLORS.lightYellow);
  const dataErrors = generateData(itemCount || 1, itemErrorCount || 0, LEGACY_COLORS.lightRed);

  const recommendationCount =
    Object.keys(recommendationDonutData).length > 0
      ? `${recommendationDonutData.item_info_count} / ${itemCount}`
      : '';
  const warningCount =
    Object.keys(recommendationDonutData).length > 0
      ? `${recommendationDonutData.item_warning_count} / ${itemCount}`
      : '';
  const errorCount =
    Object.keys(recommendationDonutData).length > 0
      ? `${recommendationDonutData.item_error_count} / ${itemCount}`
      : '';

  return (
    <div className="analyse-recommendation analyse__border">
      <p className="title home-container__analyse-title">
        {analyseTranslation.recommendations}
        {Object.keys(recommendationDonutData).length === 0 && checkIfAnalysesExist()}
      </p>
      <div className="analyse-recommendation__graphs flex">
        <div className="analyse-recommendation__single-graph">
          <p className="analyse-recommendation__single-graph-title">
            {analyseTranslation.recommendations}
          </p>
          {dataAllRecommendation && (
            <div className="analyse-recommendation__single-graph-wrapper">
              <Doughnut width={100} height={100} data={dataAllRecommendation} options={options} />
              <Tooltip
                trigger="click"
                title={navigationButton(t('homeContainer:showRecommendations'), 1)}
                color="white"
              >
                <span className="analyse-recommendation__single-graph-count-info analyse-recommendation__error">
                  {recommendationCount}
                </span>
              </Tooltip>
            </div>
          )}
        </div>
        <div className="analyse-recommendation__single-graph">
          <p className="analyse-recommendation__single-graph-title">
            {analyseTranslation.warnings}
          </p>
          {dataWarnings && (
            <div className="analyse-recommendation__single-graph-wrapper">
              <Doughnut width={100} height={100} data={dataWarnings} options={options} />
              <Tooltip
                trigger="click"
                title={navigationButton(t('homeContainer:showWarnings'), 2)}
                color="white"
              >
                <span className="analyse-recommendation__single-graph-count-info analyse-recommendation__error">
                  {warningCount}
                </span>
              </Tooltip>
            </div>
          )}
          {/* will be needed in the future */}
          {/* <p className="analyse-recommendation__single-graph-text-info">
              <span className="icon-warning--info" />
              {this.props.recommendationDonutData && this.props.recommendationDonutData.item_warning_count}
            </p> */}
        </div>
        <div className="analyse-recommendation__single-graph">
          <p className="analyse-recommendation__single-graph-title">{analyseTranslation.errors}</p>
          {dataErrors && (
            <div className="analyse-recommendation__single-graph-wrapper">
              <Doughnut width={100} height={100} data={dataErrors} options={options} />
              <Tooltip
                trigger="click"
                title={navigationButton(t('homeContainer:showErrors'), 3)}
                color="white"
              >
                <span className="analyse-recommendation__single-graph-count-info analyse-recommendation__error">
                  {errorCount}
                </span>
              </Tooltip>
            </div>
          )}
          {/* will be needed in the future */}
          {/* <p className="analyse-recommendation__single-graph-text-info">
              <span className="icon-warning--info" />
              {this.props.recommendationDonutData && this.props.recommendationDonutData.item_error_count}
            </p> */}
        </div>
      </div>
      {/* will be needed in the future */}
      {/* <AnalyseRecommendationItems items={this.props.recommendationItems} /> */}
    </div>
  );
};

export default AnalyseRecommendation;
